import { useEffect, useState } from "react";
import { affectsCars, directionsEmbedUrl, directionsLink, driveText, ROAD_LABEL, type DriveInfo, type DriveTarget, type Point } from "../../shared/drive";
import { useHousehold } from "../app/useHousehold";
import { useDrives } from "../data/newsQueries";
import { Icon } from "./Icon";
import { openOutside } from "./OutsideLink";
import { Sheet } from "./Sheet";

// Directions from home to any place, without leaving Kinmantle: Google's map
// in a frame (the wall has no browser around the app to go back to), the drive
// time and chain laws from CDOT, and the full Google Maps link as a QR code
// for the phone that will actually do the driving.

const EVENT = "km:directions";

export function openDirections(target: DriveTarget) {
  window.dispatchEvent(new CustomEvent<DriveTarget>(EVENT, { detail: target }));
}

export function DirectionsButton({ target, className = "btn btn-sm", iconOnly }: { target: DriveTarget; className?: string; iconOnly?: boolean }) {
  return (
    <button
      type="button"
      className={className}
      aria-label={iconOnly ? `Directions to ${target.name}` : undefined}
      title={iconOnly ? "Directions" : undefined}
      onClick={(e) => {
        e.stopPropagation();
        openDirections(target);
      }}
    >
      <Icon name="navigate" size="1rem" />
      {!iconOnly && "Directions"}
    </button>
  );
}

/** Google's directions map from home, in a frame that can't take the page anywhere. */
export function DirectionsMap({ from, to }: { from: Point; to: DriveTarget }) {
  const src = directionsEmbedUrl(from, to);
  const [loaded, setLoaded] = useState<string | null>(null);
  return (
    <div className="dir-map">
      {loaded !== src && <div className="frame-wait">Loading the map…</div>}
      <iframe key={src} src={src} title={`Map: directions to ${to.name}`} sandbox="allow-scripts allow-same-origin" onLoad={() => setLoaded(src)} />
    </div>
  );
}

export function kmText(km: number | null, unit: "F" | "C"): string | null {
  if (km === null) return null;
  return unit === "F" ? `${Math.round(km / 1.609)} mi` : `${Math.round(km)} km`;
}

/** Drive time, distance and chain laws/closures on the way. */
export function DriveFacts({ info, loading }: { info: DriveInfo | undefined; loading: boolean }) {
  const { settings } = useHousehold();
  if (!info || info.minutes === null) {
    return <p className="dir-facts muted">{loading ? "Checking the drive…" : info?.at === null ? "Couldn’t place this address for a drive time; the map may still find it." : "No drive time for this trip (CDOT’s routing covers Colorado)."}</p>;
  }
  const roads = info.roads.filter(affectsCars);
  return (
    <div className="dir-facts">
      <span className="dir-time">
        <Icon name="car" size="1.2rem" />
        <b>{driveText(info.minutes)}</b>
        <span className="muted">{kmText(info.km, settings.temperatureUnit)} · without traffic</span>
      </span>
      {roads.length ? (
        <ul className="dir-roads" aria-label="On the way">
          {roads.map((e) => (
            <li key={e.id} className={`mtn-road is-${e.kind}`}>
              <span className="mtn-road-kind">{ROAD_LABEL[e.kind]}</span>
              <strong>{e.route}</strong>
              <span className="mtn-road-where">{e.where ?? e.title}</span>
            </li>
          ))}
        </ul>
      ) : (
        <span className="muted">No chain laws or closures on the way</span>
      )}
    </div>
  );
}

function DirectionsSheet({ target, onClose }: { target: DriveTarget; onClose: () => void }) {
  const home = useHousehold().settings.location;
  const q = useDrives([{ id: "to", target }]);
  return (
    <Sheet
      title={`Directions to ${target.name}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn" onClick={() => openOutside(directionsLink(home, target), `Directions to ${target.name}`)}>
            <Icon name="phone" size="1.1rem" /> On your phone
          </button>
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <div className="dir-sheet">
        {home ? (
          <>
            <DriveFacts info={q.data?.routes.to} loading={q.isPending} />
            <DirectionsMap from={home} to={target} />
            <p className="muted small">Map and route: Google Maps. Drive time and roads: CDOT COtrip.</p>
          </>
        ) : (
          <p className="muted">Set the household’s location in Settings to get directions from home.</p>
        )}
      </div>
    </Sheet>
  );
}

export function DirectionsHost() {
  const [target, setTarget] = useState<DriveTarget | null>(null);
  useEffect(() => {
    const on = (e: Event) => setTarget((e as CustomEvent<DriveTarget>).detail);
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  return target ? <DirectionsSheet target={target} onClose={() => setTarget(null)} /> : null;
}
